import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { GUID, InternalUserProfile, PersonOrg } from '@ndvr/auth-api';

import { getPersonName } from 'utils';

import { setRumUser } from '../../datadog-logging';
import { logout, useCurrentInternalUserProfile, useCurrentPersonOrgGUID, useUsersTenants } from './auth-queries';
import { NoTenantAccess } from './NoTenantAccess';
import { getStoredTenantGUIDs, normalizeTenantGUIDs, setStoredTenantGUIDs, TENANT_STORAGE_KEY } from './tenant-storage';

type TenantInfo = {
    tenantGUID: string;
    name: string;
};

const EMPTY_TENANT: TenantInfo = { tenantGUID: '', name: '' };

const areTenantGUIDListsEqual = (left: string[], right: string[]) =>
    left.length === right.length && left.every((tenantGUID, index) => tenantGUID === right[index]);

type AuthenticatedAuthProviderProps = PropsWithChildren<{
    userGUID: GUID<PersonOrg>;
    setAuthenticatedUser: (userGUID: GUID<PersonOrg>) => void;
    isLoadingCurrentUserGUID: boolean;
}>;

export type AuthenticatedInternalUser = InternalUserProfile & {
    personOrgGUID: GUID<PersonOrg>;
};

type AuthContextValue = {
    isAuthenticated: boolean;
    isLoadingUser: boolean;
    setAuthenticatedUser: (userGUID: GUID<PersonOrg>) => void;
    currentPersonOrgGUID?: GUID<PersonOrg>;
    user?: AuthenticatedInternalUser;
    currentTenant: TenantInfo;
    selectedTenants: TenantInfo[];
    setTenants: (tenantGUIDs: string[]) => void;
    setTenant: (tenantGUID: string) => void;
    availableTenants: TenantInfo[];
};

export const AuthContext = React.createContext<AuthContextValue>({
    isAuthenticated: false,
    isLoadingUser: false,
    setAuthenticatedUser: () => {},
    currentPersonOrgGUID: undefined,
    currentTenant: EMPTY_TENANT,
    selectedTenants: [],
    setTenants: () => {},
    setTenant: () => {},
    availableTenants: [],
});

export const RequireAuth = ({ children }: PropsWithChildren) => {
    const { isAuthenticated, isLoadingUser } = useContext(AuthContext);
    const location = useLocation();

    if (isLoadingUser) {
        return null;
    }

    if (!isAuthenticated) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    return <>{children}</>;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
    const [userGUID, setUserGUID] = useState<GUID<PersonOrg> | undefined>();

    // Kept enabled (with the default refetch-on-focus) so a login or impersonation in
    // another tab is noticed: the session cookie is shared, and the app must not keep
    // acting as the previously loaded user.
    const {
        data: hydratedUserGUID,
        isLoading: isLoadingCurrentUser,
        isFetching: isFetchingCurrentUser,
    } = useCurrentPersonOrgGUID();

    useEffect(() => {
        if (hydratedUserGUID) {
            setUserGUID(hydratedUserGUID);
        }
    }, [hydratedUserGUID]);

    // Everything in this tab (query cache, component state, RUM identity) belongs to the
    // previous session, and clearing caches mid-flight aborts the very requests the new
    // session needs. A changed identity means a different session: reboot the app, the
    // same way logout() does.
    const previousUserGUIDRef = useRef<GUID<PersonOrg>>();
    useEffect(() => {
        if (!userGUID) {
            return;
        }
        if (previousUserGUIDRef.current && previousUserGUIDRef.current !== userGUID) {
            location.reload();
            return;
        }
        previousUserGUIDRef.current = userGUID;
    }, [userGUID]);

    const setAuthenticatedUser = useCallback((guid: GUID<PersonOrg>) => {
        setUserGUID(guid);
    }, []);

    const isLoadingUser = !userGUID && (isLoadingCurrentUser || isFetchingCurrentUser || !!hydratedUserGUID);

    const unauthenticatedValue = useMemo<AuthContextValue>(() => {
        return {
            isAuthenticated: false,
            isLoadingUser,
            setAuthenticatedUser,
            currentPersonOrgGUID: userGUID,
            currentTenant: EMPTY_TENANT,
            selectedTenants: [],
            setTenants: () => {},
            setTenant: () => {},
            availableTenants: [],
        };
    }, [isLoadingUser, setAuthenticatedUser, userGUID]);

    if (!userGUID) {
        return <AuthContext.Provider value={unauthenticatedValue}>{children}</AuthContext.Provider>;
    }

    return (
        <AuthenticatedAuthProvider
            userGUID={userGUID}
            setAuthenticatedUser={setAuthenticatedUser}
            isLoadingCurrentUserGUID={isLoadingUser}
        >
            {children}
        </AuthenticatedAuthProvider>
    );
};

const selectTenants = (availableTenants: TenantInfo[], storedTenantGUIDs: string[]): TenantInfo[] => {
    if (availableTenants.length === 0) {
        return [];
    }

    const availableTenantMap = new Map(availableTenants.map((tenant) => [tenant.tenantGUID, tenant]));
    const selectedTenants = normalizeTenantGUIDs(storedTenantGUIDs)
        .map((tenantGUID) => availableTenantMap.get(tenantGUID))
        .filter((tenant): tenant is TenantInfo => Boolean(tenant));

    return selectedTenants.length > 0 ? selectedTenants : [availableTenants[0]];
};

const AuthenticatedAuthProvider = ({
    children,
    userGUID,
    setAuthenticatedUser,
    isLoadingCurrentUserGUID,
}: AuthenticatedAuthProviderProps) => {
    const {
        data: internalUserProfile,
        isError: hasInternalUserProfileError,
        isFetched: isInternalUserProfileFetched,
    } = useCurrentInternalUserProfile(userGUID);
    const { data: tenantsData, isError: hasTenantsError, isFetched: isTenantsFetched } = useUsersTenants(userGUID);
    const [selectedTenantGUIDs, setSelectedTenantGUIDs] = useState<string[]>(getStoredTenantGUIDs);
    const availableTenants = useMemo(() => tenantsData ?? [], [tenantsData]);
    const user = useMemo(
        () => (internalUserProfile ? { ...internalUserProfile, personOrgGUID: userGUID } : undefined),
        [internalUserProfile, userGUID],
    );

    useEffect(() => {
        if (user) {
            setRumUser({
                id: user.personOrgGUID,
                email: user.email,
                name: getPersonName(user),
            });
        }
    }, [user]);

    const selectedTenants = useMemo<TenantInfo[]>(() => {
        return selectTenants(availableTenants, selectedTenantGUIDs);
    }, [availableTenants, selectedTenantGUIDs]);

    const currentTenant = selectedTenants[0] ?? EMPTY_TENANT;

    const setTenants = useCallback(
        (tenantGUIDs: string[]) => {
            const nextTenantGUIDs = selectTenants(availableTenants, tenantGUIDs).map((tenant) => tenant.tenantGUID);
            setSelectedTenantGUIDs((currentTenantGUIDs) =>
                areTenantGUIDListsEqual(currentTenantGUIDs, nextTenantGUIDs) ? currentTenantGUIDs : nextTenantGUIDs,
            );
            setStoredTenantGUIDs(nextTenantGUIDs);
        },
        [availableTenants],
    );

    const setTenant = useCallback(
        (tenantGUID: string) => {
            if (!tenantGUID) {
                return;
            }
            setTenants([tenantGUID]);
        },
        [setTenants],
    );

    useEffect(() => {
        const normalizedTenantGUIDs = selectedTenants.map((tenant) => tenant.tenantGUID);
        const storedTenantGUIDs = localStorage.getItem(TENANT_STORAGE_KEY);
        const normalizedTenantGUIDsJson = JSON.stringify(normalizedTenantGUIDs);

        if (!isTenantsFetched) {
            return;
        }

        if (
            areTenantGUIDListsEqual(normalizedTenantGUIDs, selectedTenantGUIDs) &&
            storedTenantGUIDs === normalizedTenantGUIDsJson
        ) {
            return;
        }
        setSelectedTenantGUIDs(normalizedTenantGUIDs);
        setStoredTenantGUIDs(normalizedTenantGUIDs);
    }, [availableTenants, isTenantsFetched, selectedTenantGUIDs, selectedTenants]);

    const hasResolvedAuthData = Boolean(user) && Boolean(tenantsData) && selectedTenants.length > 0;
    const hasNoTenantAssigned =
        isInternalUserProfileFetched &&
        Boolean(user) &&
        isTenantsFetched &&
        !hasTenantsError &&
        tenantsData !== undefined &&
        selectedTenants.length === 0;
    const hasMissingAuthData = isInternalUserProfileFetched && !user;
    const hasAuthBootstrapError = hasInternalUserProfileError || hasTenantsError || hasMissingAuthData;
    const isAuthenticated = hasResolvedAuthData;
    const isLoadingUser =
        isLoadingCurrentUserGUID || (!hasAuthBootstrapError && !hasResolvedAuthData && !hasNoTenantAssigned);

    const value = useMemo<AuthContextValue>(() => {
        if (!isAuthenticated) {
            return {
                isAuthenticated: false,
                isLoadingUser,
                setAuthenticatedUser,
                currentPersonOrgGUID: userGUID,
                currentTenant: EMPTY_TENANT,
                selectedTenants: [],
                setTenants: () => {},
                setTenant: () => {},
                availableTenants: [],
            };
        }

        return {
            isAuthenticated: true,
            isLoadingUser,
            setAuthenticatedUser,
            currentPersonOrgGUID: userGUID,
            user,
            currentTenant,
            selectedTenants,
            setTenants,
            setTenant,
            availableTenants,
        };
    }, [
        availableTenants,
        currentTenant,
        isAuthenticated,
        isLoadingUser,
        selectedTenants,
        setAuthenticatedUser,
        setTenants,
        setTenant,
        user,
        userGUID,
    ]);

    if (hasNoTenantAssigned) {
        return <NoTenantAccess onSignOut={() => void logout()} />;
    }

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => useContext(AuthContext);
